import React, { useMemo, useState } from 'react';
import { useAuth } from '../auth/hooks/useAuth';
import { useAbortableFetch } from '../../hooks/useAbortableFetch';
import { dashboardService } from '../../services/dashboardService';
import { mapSupabaseError } from '../../utils/supabaseErrors';
import { KpiSummaryCards } from './KpiSummaryCards';
import { KpiFilterBar } from './KpiFilterBar';
import { ApprovedKpiList } from './ApprovedKpiList';
import { KpiSubmissionDetailModal } from './KpiSubmissionDetailModal';
import { PerformanceSummaryCards } from './PerformanceSummaryCards';
import { PerformanceFilterBar } from './PerformanceFilterBar';
import { ApprovedPerformanceList } from './ApprovedPerformanceList';
import { PerformanceSubmissionDetailModal } from './PerformanceSubmissionDetailModal';
import { ReportsSummaryCards } from './ReportsSummaryCards';
import { ReportsFilterBar } from './ReportsFilterBar';
import { ApprovedReportsList } from './ApprovedReportsList';
import { ReportsSubmissionDetailModal } from './ReportsSubmissionDetailModal';
import './Dashboard.css';
import { DATE_RANGES } from '../../utils/dateRanges';
import { reportAdminService } from '../../services/reportAdminService';
import { reportService } from '../../services/reportService';
import { mergeReportContent, normalizeReportForExport, buildSingleReportRows, buildCombinedReportRows, reportFilename, combinedFilename } from '../../utils/reportExport';
import { toCsv, downloadCsv } from '../../utils/csv';
import toast from 'react-hot-toast';

function toServerKpiFilters(f) {
  const r = f.dateKey && DATE_RANGES[f.dateKey] ? DATE_RANGES[f.dateKey]() : {};
  return { name: f.name, teamLead: f.teamLead, countryId: f.countryId, kpiSystemId: f.kpiSystemId, completed: f.completed, dateFrom: r.from, dateTo: r.to };
}
function toServerPerfFilters(f) {
  const r = f.dateKey && DATE_RANGES[f.dateKey] ? DATE_RANGES[f.dateKey]() : {};
  return { name: f.name, teamLead: f.teamLead, countryId: f.countryId, piSystemId: f.piSystemId, dateFrom: r.from, dateTo: r.to };
}
function toServerReportFilters(f) {
  let from, to;
  if (f.dateKey === 'custom') {
    if (f.customFrom && f.customTo && f.customFrom <= f.customTo) { from = f.customFrom; to = f.customTo; }
  } else if (f.dateKey && DATE_RANGES[f.dateKey]) {
    const r = DATE_RANGES[f.dateKey](); from = r.from; to = r.to;
  }
  return { name: f.name, teamLead: f.teamLead, countryId: f.countryId, dateFrom: from, dateTo: to };
}
function reportRowMatchesFilters(r, f) {
  if (f.name && !(r.submitter?.name || '').toLowerCase().includes(f.name.toLowerCase())) return false;
  if (f.countryId && r.countryId !== f.countryId) return false;
  if (f.teamLead && r.teamLeadId !== f.teamLead) return false;
  return true;
}

export function DashboardPage() {
  const { user, userRole } = useAuth();
  const isMember = (userRole?.role || 'Member') === 'Member';
  const [tab, setTab] = useState('kpis');

  // KPI tab state
  const [filters, setFilters] = useState({});
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(0);
  const [loadingCards, setLoadingCards] = useState(true);
  const [loadingList, setLoadingList] = useState(!isMember);
  const [error, setError] = useState('');
  const [detail, setDetail] = useState(null);

  // Performance tab state
  const [perfFilters, setPerfFilters] = useState({});
  const [perfSummary, setPerfSummary] = useState(null);
  const [perfRows, setPerfRows] = useState([]);
  const [perfCount, setPerfCount] = useState(0);
  const [perfPage, setPerfPage] = useState(0);
  const [perfLoadingCards, setPerfLoadingCards] = useState(true);
  const [perfLoadingList, setPerfLoadingList] = useState(true);
  const [perfError, setPerfError] = useState('');
  const [perfDetail, setPerfDetail] = useState(null);

  // Reports tab state
  const [reportFilters, setReportFilters] = useState({});
  const [reportSummary, setReportSummary] = useState(null);
  const [reportRows, setReportRows] = useState([]);
  const [reportCount, setReportCount] = useState(0);
  const [reportPage, setReportPage] = useState(0);
  const [reportLoadingCards, setReportLoadingCards] = useState(true);
  const [reportLoadingList, setReportLoadingList] = useState(true);
  const [reportError, setReportError] = useState('');
  const [reportDetail, setReportDetail] = useState(null);
  const [exportingReports, setExportingReports] = useState(false);

  const serverFilters = useMemo(() => toServerKpiFilters(filters), [filters]);
  const anyFilter = useMemo(() => Object.values(filters).some((v) => v != null && v !== ''), [filters]);
  const serverPerfFilters = useMemo(() => toServerPerfFilters(perfFilters), [perfFilters]);
  const anyPerfFilter = useMemo(() => Object.values(perfFilters).some((v) => v != null && v !== ''), [perfFilters]);

  const perfActive = !isMember && tab === 'performance';
  const serverReportFilters = useMemo(() => toServerReportFilters(reportFilters), [reportFilters]);
  const anyReportFilter = useMemo(() => Object.values(reportFilters).some((v) => v != null && v !== ''), [reportFilters]);

  const reportsActive = !isMember && tab === 'reports';

  useAbortableFetch(({ isCancelled }) => {
    if (!user) return;
    (async () => {
      try {
        setLoadingCards(true);
        const s = await dashboardService.kpiSummary(serverFilters);
        if (!isCancelled()) setSummary(s);
      } catch (e) { if (!isCancelled()) setError(mapSupabaseError(e)); }
      finally { if (!isCancelled()) setLoadingCards(false); }
    })();
  }, [user?.id, JSON.stringify(serverFilters)]);

  useAbortableFetch(({ isCancelled }) => {
    if (!user || isMember) return;
    (async () => {
      try {
        setLoadingList(true);
        const { rows: data, count: c } = await dashboardService.listApprovedKpis(serverFilters, page);
        if (!isCancelled()) {
          setRows(data.map((r) => ({
            ...r,
            approvedByName: r.reviewer?.name || '—',
            teamLeadId: r.submitter?.team_lead?.id || null,
            teamLeadName: r.submitter?.team_lead?.name || null,
          })));
          setCount(c);
        }
      } catch (e) { if (!isCancelled()) setError(mapSupabaseError(e)); }
      finally { if (!isCancelled()) setLoadingList(false); }
    })();
  }, [user?.id, isMember, JSON.stringify(serverFilters), page]);

  useAbortableFetch(({ isCancelled }) => {
    if (!user || !perfActive) return;
    (async () => {
      try {
        setPerfLoadingCards(true);
        const s = await dashboardService.performanceSummary(serverPerfFilters);
        if (!isCancelled()) setPerfSummary(s);
      } catch (e) { if (!isCancelled()) setPerfError(mapSupabaseError(e)); }
      finally { if (!isCancelled()) setPerfLoadingCards(false); }
    })();
  }, [user?.id, perfActive, JSON.stringify(serverPerfFilters)]);

  useAbortableFetch(({ isCancelled }) => {
    if (!user || !perfActive) return;
    (async () => {
      try {
        setPerfLoadingList(true);
        const { rows: data, count: c } = await dashboardService.listApprovedPerformance(serverPerfFilters, perfPage);
        if (!isCancelled()) {
          setPerfRows(data.map((r) => ({
            ...r,
            approvedByName: r.reviewer?.name || '—',
            teamLeadId: r.submitter?.team_lead?.id || null,
            teamLeadName: r.submitter?.team_lead?.name || null,
          })));
          setPerfCount(c);
        }
      } catch (e) { if (!isCancelled()) setPerfError(mapSupabaseError(e)); }
      finally { if (!isCancelled()) setPerfLoadingList(false); }
    })();
  }, [user?.id, perfActive, JSON.stringify(serverPerfFilters), perfPage]);

  useAbortableFetch(({ isCancelled }) => {
    if (!user || !reportsActive) return;
    (async () => {
      try {
        setReportLoadingCards(true);
        const s = await dashboardService.reportsSummary(serverReportFilters);
        if (!isCancelled()) setReportSummary(s);
      } catch (e) { if (!isCancelled()) setReportError(mapSupabaseError(e)); }
      finally { if (!isCancelled()) setReportLoadingCards(false); }
    })();
  }, [user?.id, reportsActive, JSON.stringify(serverReportFilters)]);

  useAbortableFetch(({ isCancelled }) => {
    if (!user || !reportsActive) return;
    (async () => {
      try {
        setReportLoadingList(true);
        const { rows: data, count: c } = await dashboardService.listApprovedReports(serverReportFilters, reportPage);
        if (!isCancelled()) {
          setReportRows(data.map((r) => ({
            ...r,
            approvedByName: r.reviewer?.name || '—',
            teamLeadId: r.submitter?.team_lead?.id || null,
            teamLeadName: r.submitter?.team_lead?.name || null,
            countryId: r.country_id || r.submitter?.country_id || null,
            countryName: r.countries?.name || r.submitter?.countries?.name || null,
          })));
          setReportCount(c);
        }
      } catch (e) { if (!isCancelled()) setReportError(mapSupabaseError(e)); }
      finally { if (!isCancelled()) setReportLoadingList(false); }
    })();
  }, [user?.id, reportsActive, JSON.stringify(serverReportFilters), reportPage]);

  const options = useMemo(() => {
    const byId = (arr) => Object.values(arr.reduce((m, x) => (x.id ? { ...m, [x.id]: x } : m), {}));
    return {
      teamLeads: byId(rows.map((r) => ({ id: r.teamLeadId, name: r.teamLeadName })).filter((x) => x.id && x.name)),
      countries: byId(rows.map((r) => ({ id: r.submitter?.country_id, name: r.submitter?.countries?.name }))),
      systems: byId(rows.map((r) => ({ id: r.kpi_system_id, name: r.kpi_systems?.name }))),
    };
  }, [rows]);

  const perfOptions = useMemo(() => {
    const byId = (arr) => Object.values(arr.reduce((m, x) => (x.id ? { ...m, [x.id]: x } : m), {}));
    return {
      teamLeads: byId(perfRows.map((r) => ({ id: r.teamLeadId, name: r.teamLeadName })).filter((x) => x.id && x.name)),
      countries: byId(perfRows.map((r) => ({ id: r.submitter?.country_id, name: r.submitter?.countries?.name }))),
      systems: byId(perfRows.map((r) => ({ id: r.pi_system_id, name: r.pi_systems?.name }))),
    };
  }, [perfRows]);

  const reportOptions = useMemo(() => {
    const byId = (arr) => Object.values(arr.reduce((m, x) => (x.id ? { ...m, [x.id]: x } : m), {}));
    return {
      teamLeads: byId(reportRows.map((r) => ({ id: r.teamLeadId, name: r.teamLeadName })).filter((x) => x.id && x.name)),
      countries: byId(reportRows.map((r) => ({ id: r.countryId, name: r.countryName }))),
    };
  }, [reportRows]);

  const pageSize = anyFilter ? 10 : 5;
  const pageCount = Math.max(1, Math.ceil(count / pageSize));
  const perfPageSize = anyPerfFilter ? 10 : 5;
  const perfPageCount = Math.max(1, Math.ceil(perfCount / perfPageSize));
  const reportPageSize = anyReportFilter ? 10 : 5;
  const reportPageCount = Math.max(1, Math.ceil(reportCount / reportPageSize));

  const visibleRows = useMemo(() => rows.filter((r) => {
    if (filters.name && !(r.submitter?.name || '').toLowerCase().includes(filters.name.toLowerCase())) return false;
    if (filters.countryId && r.submitter?.country_id !== filters.countryId) return false;
    if (filters.teamLead && r.teamLeadId !== filters.teamLead) return false;
    if (filters.completed != null) {
      const fully = (r.kpis || []).every((k) => Number(k.current_value) >= Number(k.target_value));
      if (fully !== filters.completed) return false;
    }
    return true;
  }), [rows, filters]);

  const visiblePerfRows = useMemo(() => perfRows.filter((r) => {
    if (perfFilters.name && !(r.submitter?.name || '').toLowerCase().includes(perfFilters.name.toLowerCase())) return false;
    if (perfFilters.countryId && r.submitter?.country_id !== perfFilters.countryId) return false;
    if (perfFilters.teamLead && r.teamLeadId !== perfFilters.teamLead) return false;
    return true;
  }), [perfRows, perfFilters]);

  const visibleReportRows = useMemo(
    () => reportRows.filter((r) => reportRowMatchesFilters(r, reportFilters)),
    [reportRows, reportFilters],
  );

  const exportSingleReport = async (row) => {
    try {
      const [fields, content] = await Promise.all([
        reportAdminService.getReportSystemFields(row.report_system_id),
        reportService.getSubmissionReport(row.id),
      ]);
      const norm = normalizeReportForExport(row, mergeReportContent(fields, content));
      downloadCsv(reportFilename(norm), toCsv(buildSingleReportRows(norm)));
    } catch (e) { toast.error(mapSupabaseError(e)); }
  };

  const exportAllReports = async () => {
    try {
      setExportingReports(true);
      const all = await dashboardService.listAllApprovedReportsForExport(serverReportFilters);
      const visible = all.filter((r) => reportRowMatchesFilters({
        ...r,
        teamLeadId: r.submitter?.team_lead?.id || null,
        countryId: r.country_id || r.submitter?.country_id || null,
      }, reportFilters));
      if (visible.length === 0) { toast.error('No reports to export.'); return; }
      const systemIds = [...new Set(visible.map((r) => r.report_system_id))];
      const fieldsBySystem = {};
      await Promise.all(systemIds.map(async (id) => { fieldsBySystem[id] = await reportAdminService.getReportSystemFields(id); }));
      const normalized = visible.map((r) => normalizeReportForExport(r, mergeReportContent(fieldsBySystem[r.report_system_id] || [], r.report_content || [])));
      downloadCsv(combinedFilename(reportFilters), toCsv(buildCombinedReportRows(normalized)));
    } catch (e) { toast.error(mapSupabaseError(e)); }
    finally { setExportingReports(false); }
  };

  return (
    <div className="dashboard-page">
      {!isMember && (
        <div className="dashboard-tabs" role="tablist" aria-label="Dashboard">
          <button type="button" className={tab === 'kpis' ? 'active' : ''} aria-current={tab === 'kpis'} onClick={() => setTab('kpis')}>KPIs</button>
          <button type="button" className={tab === 'performance' ? 'active' : ''} aria-current={tab === 'performance'} onClick={() => setTab('performance')}>Performance</button>
          <button type="button" className={tab === 'reports' ? 'active' : ''} aria-current={tab === 'reports'} onClick={() => setTab('reports')}>Reports</button>
        </div>
      )}
      {isMember && <p><span className="team-chip">Your team</span></p>}

      {(isMember || tab === 'kpis') && (
        <>
          {error && <div className="error-message" role="alert">{error}</div>}
          <KpiSummaryCards summary={summary} loading={loadingCards} />
          <KpiFilterBar filters={filters} options={options} isMember={isMember}
            onChange={(f) => { setFilters(f); setPage(0); }} />
          {!isMember && (loadingList
            ? <p className="loading-msg">Loading…</p>
            : <ApprovedKpiList rows={visibleRows} page={page} pageCount={pageCount} onPage={setPage} onDetail={setDetail} />)}
          {isMember && <p className="submissions-empty">Aggregate figures for your team's approved KPIs.</p>}
          {detail && <KpiSubmissionDetailModal submission={detail} onClose={() => setDetail(null)} />}
        </>
      )}

      {perfActive && (
        <>
          {perfError && <div className="error-message" role="alert">{perfError}</div>}
          <PerformanceSummaryCards summary={perfSummary} loading={perfLoadingCards} />
          <PerformanceFilterBar filters={perfFilters} options={perfOptions}
            onChange={(f) => { setPerfFilters(f); setPerfPage(0); }} />
          {perfLoadingList
            ? <p className="loading-msg">Loading…</p>
            : <ApprovedPerformanceList rows={visiblePerfRows} page={perfPage} pageCount={perfPageCount} onPage={setPerfPage} onDetail={setPerfDetail} />}
          {perfDetail && <PerformanceSubmissionDetailModal submission={perfDetail} onClose={() => setPerfDetail(null)} />}
        </>
      )}

      {reportsActive && (
        <>
          {reportError && <div className="error-message" role="alert">{reportError}</div>}
          <ReportsSummaryCards summary={reportSummary} loading={reportLoadingCards} />
          <ReportsFilterBar filters={reportFilters} options={reportOptions}
            onChange={(f) => { setReportFilters(f); setReportPage(0); }} />
          {reportLoadingList
            ? <p className="loading-msg">Loading…</p>
            : <ApprovedReportsList rows={visibleReportRows} page={reportPage} pageCount={reportPageCount} onPage={setReportPage} onDetail={setReportDetail}
                onExportRow={exportSingleReport} onExportAll={exportAllReports} exporting={exportingReports} />}
          {reportDetail && <ReportsSubmissionDetailModal submission={reportDetail} onClose={() => setReportDetail(null)} />}
        </>
      )}
    </div>
  );
}
